import React, { useState } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Chip, IconButton, Tooltip, Link,
    Paper, Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
    CircularProgress, Alert,
    InputAdornment, Collapse
} from '@mui/material';
import { CustomTextField } from './CustomTextFields';
import { PrimaryButton, SecondaryButton } from './Buttons';
import {
    Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
    Build as EquipmentIcon, Inventory as ConsumableIcon,
    AssignmentTurnedIn as AssignmentIcon, Remove as RemoveIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Description as DocumentIcon, Visibility as VisibilityIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Helper to detect if a consumable unit represents an area measurement
const isAreaUnit = (item) => {
    if (!item) return false;
    const type = item.unit_details?.type || '';
    const symbol = (item.unit_details?.symbol || '').toLowerCase();
    const name = (item.unit_details?.name || '').toLowerCase();
    if (type === 'area') return true;
    const areaKeywords = ['sq in', 'sq.in', 'in²', 'square inch', 'square inches', 'sq ft', 'sq cm', 'cm²', 'mm²'];
    return areaKeywords.some(k => symbol.includes(k) || name.includes(k));
};

// Parse "NxM" or "N x M" film size string into numeric area. Returns null if invalid.
const parseFilmArea = (value) => {
    if (value === null || value === undefined) return null;
    const match = String(value).trim().match(/^(\d+(\.\d+)?)\s*[xX]\s*(\d+(\.\d+)?)$/);
    if (!match) return null;
    return parseFloat(match[1]) * parseFloat(match[3]);
};

// Helper function to get color for contextual status
const getContextualStatusColor = (status) => {
    switch (status) {
        case 'allocated':
            return 'primary';
        case 'used before':
            return 'info';
        case 'did not use':
            return 'warning';
        case 'available':
            return 'success';
        case 'unavailable':
            return 'error';
        default:
            return 'default';
    }
};

// Helper function to format contextual status for display
const formatContextualStatus = (status) => {
    switch (status) {
        case 'allocated':
            return 'Allocated';
        case 'used before':
            return 'Used Before';
        case 'did not use':
            return 'Did Not Use';
        case 'available':
            return 'Available';
        case 'unavailable':
            return 'Unavailable';
        default:
            return status;
    }
};

const AssignmentManager = ({
    title,
    type = 'equipment', // 'equipment' or 'consumable'
    currentAssignments = [],
    availableItems = [],
    onAdd = null,
    onRemove = null,
    onUpdate = null,
    canEdit = false,
    showAddButton = true,
    maxHeight = '400px',
    serviceOrderId = null
}) => {
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expandedItems, setExpandedItems] = useState({});

    /** How much can still be committed for this line (server uses same notion when service_order_id is in context). */
    const getConsumableMaxAdditional = (item) => {
        if (!item) return 0;
        const aq = item.available_quantity;
        if (aq !== undefined && aq !== null && aq !== '') {
            const n = parseFloat(aq);
            if (!Number.isNaN(n) && n >= 0) return n;
        }
        return Math.max(0, parseFloat(item.current_quantity) || 0);
    };

    const getTypeIcon = () => {
        return type === 'equipment' ? <EquipmentIcon color="primary" /> : <ConsumableIcon color="primary" />;
    };

    const getTypeLabel = () => {
        return type === 'equipment' ? 'Equipment' : 'Consumable';
    };

    // Use primary for both so Equipment and Consumables sections match on Service Order detail (theme-consistent)
    const getTypeColor = () => {
        return 'primary';
    };

    const handleAddClick = () => {
        setSelectedItems([]);
        setQuantities({});
        setError('');
        setAddDialogOpen(true);
    };

    const handleItemSelection = (itemId, checked) => {
        if (checked) {
            setSelectedItems(prev => [...prev, itemId]);
            if (type === 'consumable') {
                const item = availableItems.find(i => i.id === itemId);
                const maxAdditional = getConsumableMaxAdditional(item);
                const defaultAdditional = maxAdditional > 0 ? Math.min(1, maxAdditional) : 1;
                setQuantities(prev => ({ ...prev, [itemId]: defaultAdditional }));
            }
        } else {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
            setQuantities(prev => {
                const newQuantities = { ...prev };
                delete newQuantities[itemId];
                return newQuantities;
            });
        }
    };

    const handleQuantityChange = (itemId, value) => {
        const item = availableItems.find(i => i.id === itemId);
        if (type === 'consumable' && isAreaUnit(item)) {
            // For area consumables: store the raw string (e.g. "3x5")
            setQuantities(prev => ({ ...prev, [itemId]: value }));
            return;
        }
        const cap = type === 'consumable'
            ? getConsumableMaxAdditional(item)
            : (item ? parseFloat(item.current_quantity) : 1);
        const newQuantity = Math.max(1, Math.min(parseInt(value, 10) || 1, Math.max(1, cap)));
        setQuantities(prev => ({ ...prev, [itemId]: newQuantity }));
    };

    const toggleDocuments = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const handleAddAssignments = async () => {
        if (selectedItems.length === 0) {
            setError(`Please select at least one ${getTypeLabel().toLowerCase()}`);
            return;
        }

        // Validate quantities for consumables
        if (type === 'consumable') {
            for (const itemId of selectedItems) {
                const item = availableItems.find(i => i.id === itemId);
                const maxAdd = getConsumableMaxAdditional(item);

                if (isAreaUnit(item)) {
                    const rawValue = quantities[itemId] || '';
                    const area = parseFilmArea(rawValue);
                    if (area === null) {
                        setError(`Please enter a valid film size like 3x5 or 4x6 for "${item.name}"`);
                        return;
                    }
                    if (area <= 0) {
                        setError(`Film size area must be greater than 0 for "${item.name}"`);
                        return;
                    }
                    if (item && area > maxAdd) {
                        setError(
                            `Requested ${area} sq in exceeds available ${maxAdd} ${item.unit_details?.symbol || 'sq in'} for "${item.name}"`
                        );
                        return;
                    }
                } else {
                    const requestedAdditional = quantities[itemId] || 1;
                    if (item && requestedAdditional > maxAdd) {
                        setError(
                            `Requested additional quantity (${requestedAdditional}) exceeds what is available (${maxAdd}) for ${item.name}`
                        );
                        return;
                    }
                }
            }
        }

        setLoading(true);
        setError('');

        try {
            if (onAdd) {
                const assignmentData = (Array.isArray(selectedItems) ? selectedItems : []).map(itemId => {
                    const item = availableItems.find(i => i.id === itemId);
                    if (type === 'consumable') {
                        let qty;
                        if (isAreaUnit(item)) {
                            qty = parseFilmArea(quantities[itemId]) || 0;
                        } else {
                            qty = quantities[itemId] || 1;
                        }
                        return { id: itemId, quantity: qty };
                    }
                    return { id: itemId };
                });

                const result = await onAdd(assignmentData);
                // Only show success message if the operation actually succeeded
                if (result === true) {
                    toast.success(`${getTypeLabel()} assigned successfully`);
                    setAddDialogOpen(false);
                    if (onUpdate) onUpdate();
                }
            }
        } catch (error) {
            const msg = `Failed to assign ${getTypeLabel().toLowerCase()}: ${error.message}`;
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAssignment = async (assignmentId, itemName) => {
        if (onRemove) {
            try {
                const result = await onRemove(assignmentId);
                // Only show success message if the operation actually succeeded
                // If onRemove returns false or undefined, it means the operation was blocked
                if (result === true) {
                    toast.success(`${getTypeLabel()} removed successfully`);
                    // Force immediate UI update by calling onUpdate
                    if (onUpdate) {
                        onUpdate();
                    }
                }
            } catch (error) {
                toast.error(`Failed to remove ${getTypeLabel().toLowerCase()}: ${error.message}`);
            }
        }
    };

    const renderAssignmentCard = (assignment, index) => {
        const item = type === 'equipment' ? assignment : assignment.consumable_data;
        if (!item) return null;

        const hasDocuments = item.documents && item.documents.length > 0;
        const isExpanded = expandedItems[assignment.id];

        return (
            <Card key={assignment.id || index} sx={{ mb: 2, borderRadius: 4 }}>
                <CardContent sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getTypeIcon()}
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        {item.name || 'Unknown'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {type === 'equipment' ? (
                                            <>
                                                {item.serial_no && `SN: ${item.serial_no}`}
                                                {item.device_type && ` • ${item.device_type}`}
                                                {item.device_model && ` • ${item.device_model}`}
                                            </>
                                        ) : (
                                            <>
                                                {item.batch_no && `Batch: ${item.batch_no}`}
                                                {assignment.quantity && ` • Qty: ${assignment.quantity}`}
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {type === 'equipment' && (
                                    <Chip 
                                        label={formatContextualStatus(item.contextual_status) || (item.is_available ? 'Available' : 'Unavailable')} 
                                        size="small" 
                                        color={getContextualStatusColor(item.contextual_status || (item.is_available ? 'available' : 'unavailable'))}
                                        variant="outlined"
                                    />
                                )}
                                {type === 'consumable' && (
                                    <Chip 
                                        label="Assigned" 
                                        size="small" 
                                        color="success"
                                        variant="outlined"
                                    />
                                )}
                                {item.location?.name && (
                                    <Typography variant="caption" color="text.secondary">
                                        📍 {item.location.name}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                {hasDocuments && (
                                    <Tooltip title={isExpanded ? "Hide documents" : `Show documents (${item.documents.length})`}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDocuments(assignment.id)}
                                        >
                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {canEdit && (
                                    <Tooltip title={`Remove ${getTypeLabel()}`}>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                // For equipment, use assignment.id (which is equipment.id)
                                                // For consumables, use item.id (which is consumable.id)
                                                const itemId = type === 'equipment' ? assignment.id : item.id;
                                                handleRemoveAssignment(itemId, item.name);
                                            }}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Documents Section */}
                    {hasDocuments && (
                        <Collapse in={isExpanded}>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ pl: 4 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DocumentIcon fontSize="small" />
                                    Documents & Credentials ({(Array.isArray(item.documents) ? item.documents : []).length})
                                </Typography>
                                <Grid container spacing={1}>
                                    {(Array.isArray(item.documents) ? item.documents : []).map((doc, docIndex) => (
                                        <Grid item xs={12} sm={6} md={4} key={doc.id || docIndex}>
                                            <Card variant="outlined" sx={{ p: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <DocumentIcon color="primary" fontSize="small" />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" noWrap>
                                                            {doc.filename}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {doc.document_type} • {doc.uploaded_by}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {doc.presigned_url && (
                                                            <Chip
                                                                icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                                                label="View"
                                                                size="small"
                                                                onClick={() => window.open(doc.presigned_url, '_blank')}
                                                                clickable
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 24 }}
                                                            />
                                                        )}
                                                        {doc.download_url && (
                                                            <Chip
                                                                icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                                label="Download"
                                                                size="small"
                                                                onClick={() => window.open(doc.download_url, '_blank')}
                                                                clickable
                                                                color="success"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 24 }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Collapse>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderAddDialog = () => (
        <Dialog 
            open={addDialogOpen} 
            onClose={() => setAddDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                color: `${getTypeColor()}.main`
            }}>
                {getTypeIcon()}
                Assign {getTypeLabel()} to Service Order
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select {getTypeLabel().toLowerCase()} to assign to this service order:
                </Typography>
                
                {/* Helpful information about equipment vs consumable behavior */}
                {type === 'equipment' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Equipment Note:</strong> Equipment can be re-allocated to other service orders once freed. 
                            When you remove equipment from a service order, it becomes available for assignment to other orders.
                        </Typography>
                    </Alert>
                )}
                
                {type === 'consumable' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Consumable Note:</strong> Consumables are consumed during service and need to be replenished in inventory. 
                            When you remove consumables, the quantity is returned to inventory but may need reordering.
                        </Typography>
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {(Array.isArray(availableItems) ? availableItems : []).map((item) => {
                        const isSelected = selectedItems.includes(item.id);
                        const isEquipmentOnOrder = type === 'equipment' && currentAssignments.some(a => a.id === item.id);
                        const isConsumableOnOrder = type === 'consumable' && currentAssignments.some(
                            a => a.consumable_data?.id === item.id
                        );
                        const isAvailable = item.is_available !== false; // Default to true if field doesn't exist
                        const maxAdditional = type === 'consumable' ? getConsumableMaxAdditional(item) : null;
                        const isBlocked = isEquipmentOnOrder || !isAvailable;

                        return (
                            <Grid item xs={12} key={item.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        cursor: isBlocked ? 'not-allowed' : 'pointer',
                                        opacity: isBlocked ? 0.6 : 1,
                                        borderColor: isSelected ? `${getTypeColor()}.main` : 'divider',
                                        backgroundColor: isSelected ? `${getTypeColor()}.50` : 'background.paper'
                                    }}
                                    onClick={() => {
                                        if (isBlocked) return;
                                        handleItemSelection(item.id, !isSelected);
                                    }}
                                >
                                    <CardContent sx={{ p: 2 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {getTypeIcon()}
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="medium">
                                                            {item.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {type === 'equipment' ? (
                                                                <>
                                                                    {item.serial_no && `SN: ${item.serial_no}`}
                                                                    {item.device_type && ` • ${item.device_type}`}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {item.batch_no && `Batch: ${item.batch_no}`}
                                                                    {(() => {
                                                                        // Prefer the server-computed available_quantity (net of
                                                                        // active reservations). Fall back to current_quantity for
                                                                        // older responses that may not include it.
                                                                        const qty = (item.available_quantity !== undefined && item.available_quantity !== null)
                                                                            ? item.available_quantity
                                                                            : item.current_quantity;
                                                                        const symbol = item.unit_details?.symbol || 'pc';
                                                                        return (qty !== undefined && qty !== null) ? ` • Available: ${qty} ${symbol}` : '';
                                                                    })()}
                                                                </>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={3}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Tooltip
                                                        title={(type === 'consumable' && item.reservation_info?.holders?.length)
                                                            ? (
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                                        Held by other service orders
                                                                    </Typography>
                                                                    {item.reservation_info.holders.map((h) => (
                                                                        <Typography key={h.service_order_id} variant="caption" component="div">
                                                                            • {h.service_order_label}: {h.quantity} {item.unit_details?.symbol || 'pc'}
                                                                        </Typography>
                                                                    ))}
                                                                </Box>
                                                            )
                                                            : ''}
                                                        arrow
                                                        disableHoverListener={!(type === 'consumable' && item.reservation_info?.holders?.length)}
                                                    >
                                                        <Chip
                                                            label={item.status || (isAvailable ? 'Available' : 'Unavailable')}
                                                            size="small"
                                                            color={isAvailable ? 'success' : 'warning'}
                                                            variant="outlined"
                                                        />
                                                    </Tooltip>
                                                    {item.availability_reason && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.availability_reason}
                                                        </Typography>
                                                    )}
                                                    {item.location?.name && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            📍 {item.location.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={3}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {type === 'consumable' && isSelected && isAvailable && (
                                                        isAreaUnit(item) ? (
                                                            // NxM text input for area/sq-inch consumables
                                                            <CustomTextField
                                                                type="text"
                                                                label="Film size"
                                                                value={quantities[item.id] || ''}
                                                                placeholder="e.g. 3x5"
                                                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                size="small"
                                                                sx={{ width: 150 }}
                                                                helperText={(() => {
                                                                    const raw = quantities[item.id] || '';
                                                                    const area = parseFilmArea(raw);
                                                                    const symbol = item.unit_details?.symbol || 'sq in';
                                                                    const avail = maxAdditional ?? 0;
                                                                    if (!raw) return `Available: ${avail} ${symbol}`;
                                                                    if (area === null) return 'Use format: 3x5 or 4x6';
                                                                    return `${area} ${symbol} — Available: ${avail} ${symbol}`;
                                                                })()}
                                                            />
                                                        ) : (
                                                            // Standard numeric input for regular consumables
                                                            <CustomTextField
                                                                type="number"
                                                                label="Add qty"
                                                                value={quantities[item.id] || 1}
                                                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                size="small"
                                                                sx={{ width: 135 }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.8rem', fontWeight: 600 } }}>
                                                                            {item.unit_details?.symbol || 'pc'}
                                                                        </InputAdornment>
                                                                    )
                                                                }}
                                                                helperText={`Max additional: ${maxAdditional ?? 0} ${item.unit_details?.symbol || 'pc'}`}
                                                            />
                                                        )
                                                    )}

                                                    {isEquipmentOnOrder ? (
                                                        <Chip label="Already assigned" size="small" color="default" />
                                                    ) : (type === 'consumable' && isConsumableOnOrder) ? (
                                                        <Chip label="On order — add more" size="small" color="default" />
                                                    ) : !isSelected ? (
                                                        <Chip
                                                            label="Select"
                                                            size="small"
                                                            color="default"
                                                            variant="outlined"
                                                        />
                                                    ) : null}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
                <SecondaryButton onClick={() => setAddDialogOpen(false)}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton
                    onClick={handleAddAssignments}
                    color={getTypeColor()}
                    disabled={loading || selectedItems.length === 0}
                    startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                >
                    {loading ? 'Assigning…' : `Assign ${getTypeLabel()}`}
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );

    return (
        <Paper variant="outlined" sx={{ borderRadius: 4 }}>
            {/* Header */}
            <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.100', 
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getTypeIcon()}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 'medium' }}>
                        {title}
                    </Typography>
                    <Chip 
                        label={`${currentAssignments.length} assigned`} 
                        size="small" 
                        color={getTypeColor()}
                        sx={{ ml: 2 }}
                    />
                </Box>
                
                {canEdit && showAddButton && (
                    <PrimaryButton
                        color={getTypeColor()}
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                        size="small"
                    >
                        Assign {getTypeLabel()}
                    </PrimaryButton>
                )}
            </Box>

            {/* Content */}
            <Box sx={{ p: 2, maxHeight, overflow: 'auto' }}>
                {(Array.isArray(currentAssignments) ? currentAssignments : []).length > 0 ? (
                    (Array.isArray(currentAssignments) ? currentAssignments : []).map((assignment, index) => 
                        renderAssignmentCard(assignment, index)
                    )
                ) : (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        color: 'text.secondary'
                    }}>
                        <AssignmentIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            No {getTypeLabel().toLowerCase()} assigned
                        </Typography>
                        <Typography variant="body2">
                            {canEdit ? 
                                `Click "Assign ${getTypeLabel()}" to add ${getTypeLabel().toLowerCase()} to this service order` :
                                `No ${getTypeLabel().toLowerCase()} has been assigned to this service order yet`
                            }
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Add Dialog */}
            {renderAddDialog()}
        </Paper>
    );
};

export default AssignmentManager; 